import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

/**
 * Render MD del vault Quant Lab con tabelle GFM, link wikilink-style risolti,
 * immagini embedded (PNG dell'equity curve), callout Obsidian-style.
 *
 * Le immagini con src relativo "figures/foo.png" vengono ri-rooted al backend
 * via la prop `figureUrlFor(filename)`.
 */
const QuantMarkdown = ({ children, figureUrlFor, onWikilinkClick }) => {
  // Pre-processa il body MD per:
  // 1. Tradurre wikilinks [[path/name|alias]] in markdown link [alias](#wiki:path/name)
  // 2. Lasciare le immagini cosi come sono — react-markdown le gestisce
  const preprocessed = preprocess(children || '');

  return (
    <div className="quant-md prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ href, children: kids, ...props }) => {
            if (href?.startsWith('#wiki:')) {
              const target = decodeURIComponent(href.slice(6));
              return (
                <button
                  type="button"
                  onClick={() => onWikilinkClick?.(target)}
                  className="text-primary hover:underline font-medium"
                >{kids}</button>
              );
            }
            return <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline" {...props}>{kids}</a>;
          },
          img: ({ src, alt, ...props }) => {
            // src "figures/foo.png" → routing al backend
            if (src && figureUrlFor) {
              const filename = src.split('/').pop();
              return <FigureImage filename={filename} alt={alt} figureUrlFor={figureUrlFor} />;
            }
            return <img src={src} alt={alt} {...props} className="rounded border my-3" />;
          },
          table: ({ children: kids }) => (
            <div className="overflow-x-auto my-3">
              <table className="min-w-full text-sm border-collapse">{kids}</table>
            </div>
          ),
          thead: ({ children: kids }) => <thead className="bg-muted/40">{kids}</thead>,
          th: ({ children: kids }) => <th className="px-2 py-1.5 text-left border-b font-semibold">{kids}</th>,
          td: ({ children: kids }) => <td className="px-2 py-1 border-b align-top">{kids}</td>,
          code: ({ inline, children: kids }) =>
            inline
              ? <code className="px-1 py-0.5 rounded bg-muted text-xs font-mono">{kids}</code>
              : <pre className="p-3 rounded bg-muted text-xs font-mono overflow-x-auto my-3"><code>{kids}</code></pre>,
          h1: ({ children: kids }) => <h1 className="text-2xl font-bold mt-6 mb-3">{kids}</h1>,
          h2: ({ children: kids }) => <h2 className="text-xl font-bold mt-5 mb-2 pb-1 border-b">{kids}</h2>,
          h3: ({ children: kids }) => <h3 className="text-lg font-semibold mt-4 mb-2">{kids}</h3>,
          blockquote: ({ children: kids }) => (
            <blockquote className="border-l-4 border-primary/30 bg-muted/30 pl-3 py-1.5 my-3 italic text-muted-foreground">
              {kids}
            </blockquote>
          ),
          ul: ({ children: kids }) => <ul className="list-disc pl-5 my-2 space-y-0.5">{kids}</ul>,
          ol: ({ children: kids }) => <ol className="list-decimal pl-5 my-2 space-y-0.5">{kids}</ol>,
        }}
      >
        {preprocessed}
      </ReactMarkdown>
    </div>
  );
};

const FigureImage = ({ filename, alt, figureUrlFor }) => {
  const [blobUrl, setBlobUrl] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const blob = await figureUrlFor(filename);
        if (cancelled || !blob) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch {
        // ignora
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [filename, figureUrlFor]);

  if (!blobUrl) {
    return <div className="my-3 p-8 text-center text-xs text-muted-foreground border rounded bg-muted/20">caricamento {filename}…</div>;
  }
  return <img src={blobUrl} alt={alt || filename} className="rounded border my-3 max-w-full" />;
};

function preprocess(md) {
  // [[path/name|alias]] → [alias](#wiki:path/name)
  // [[path/name]]       → [name](#wiki:path/name)
  return md.replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_, target, alias) => {
    const name = alias || target.split('/').pop();
    return `[${name}](#wiki:${encodeURIComponent(target)})`;
  });
}

export default QuantMarkdown;
